import { useMutation, useQuery } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  kbQuelleDetailResponseSchema,
  kbQuellenListResponseSchema,
} from '@mindkeep/shared'
import { apiRequest, apiRequestBlob } from '@/lib/api-client'

/**
 * Quellenregister und Lizenzklassen (RNA M6).
 *
 * Nur lesende Hooks plus der PDF-Export. Es gibt bewusst keine Mutation, die
 * eine Lizenzklasse setzt: die Klasse steht im Frontmatter der Quelldatei, der
 * Ingest liest sie von dort, und die Datenbankregeln aus Migration 031 haengen
 * an diesem einen Wert. Ein Schalter in der Oberflaeche waere ein zweiter
 * Schreibweg auf dieselbe Lizenz.
 */

export const kbQuellenKeys = {
  all: ['kb-quellen'] as const,
  list: () => [...kbQuellenKeys.all, 'list'] as const,
  detail: (path: string) => [...kbQuellenKeys.all, 'detail', path] as const,
}

export function useKbQuellen() {
  return useQuery({
    queryKey: kbQuellenKeys.list(),
    queryFn: () =>
      apiRequest({ path: '/kb/quellen', schema: kbQuellenListResponseSchema }),
    staleTime: 15_000,
  })
}

export function useKbQuelleDetail(path: string | null) {
  return useQuery({
    queryKey: kbQuellenKeys.detail(path ?? ''),
    enabled: !!path,
    queryFn: () =>
      apiRequest({
        path: `/kb/quellen/dokument?path=${encodeURIComponent(path as string)}`,
        schema: kbQuelleDetailResponseSchema,
      }),
  })
}

/** Derivat samt Nachweis als PDF — die Vorlage fuer eine spaetere rechtliche Pruefung. */
export function useNachweisExport() {
  return useMutation({
    mutationFn: (path: string) =>
      apiRequestBlob({ path: `/kb/quellen/export?path=${encodeURIComponent(path)}` }),
    onSuccess: ({ blob, filename }) => {
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = filename ?? 'Nachweis.pdf'
      link.click()
      URL.revokeObjectURL(url)
      toast.success('Nachweis als PDF erstellt.')
    },
    onError: (err) =>
      toast.error(err instanceof Error ? err.message : 'Export fehlgeschlagen'),
  })
}
